import { useMemo, useState } from 'react';

export default function useSortAndFilterBugs(bugs = [], userId) {
  const [onlyMine, setOnlyMine] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [onlyUrgent, setOnlyUrgent] = useState(false);
  const [sort, setSort] = useState('priority');

  const filtered = useMemo(() => {
    return bugs.filter((b) => {
      const status = (b.status || '').toLowerCase();
      if (onlyMine && b.createdBy?.id !== userId) return false;
      if (!showClosed && status === 'closed') return false;
      if (onlyUrgent && !['urgent', 'very_urgent'].includes(status)) return false;
      return true;
    });
  }, [bugs, onlyMine, showClosed, onlyUrgent, userId]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    const getPriority = (bug) =>
      Number.isFinite(bug?.priority) ? Math.min(10, Math.max(1, bug.priority)) : 1;
    const hasActivity = (bug) => {
      const created = new Date(bug?.createdAt || 0).getTime();
      const statusChanged = new Date(bug?.statusChangedAt || 0).getTime();
      const comments = Array.isArray(bug?.comments) ? bug.comments : [];
      const commentActivity = comments.some(
        (comment) => new Date(comment?.createdAt || 0).getTime() > created
      );
      return commentActivity || statusChanged - created > 1000;
    };
    const lastActivity = (bug) => {
      const times = [];
      const statusChanged = new Date(bug?.statusChangedAt || 0).getTime();
      if (statusChanged) times.push(statusChanged);
      if (bug?.lastActivityAt) times.push(new Date(bug.lastActivityAt).getTime());
      const comments = Array.isArray(bug?.comments) ? bug.comments : [];
      comments.forEach((comment) => {
        const t = new Date(comment?.createdAt || 0).getTime();
        if (t) times.push(t);
      });
      return times.length ? Math.max(...times) : new Date(bug?.createdAt || 0).getTime();
    };

    const normalizeStatusValue = (value) => (value || '').toString().toLowerCase();
    const statusOrder = {
      very_urgent: 0,
      urgent: 1,
      open: 2,
      in_progress: 3,
      testing: 4,
      fixed: 5,
      closed: 6,
    };

    if (sort === 'dateAsc') {
      arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sort === 'dateDesc') {
      arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sort === 'status') {
      arr.sort((a, b) => {
        const sa = statusOrder[normalizeStatusValue(a.status)] ?? 99;
        const sb = statusOrder[normalizeStatusValue(b.status)] ?? 99;
        if (sa !== sb) return sa - sb;
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      });
    } else {
      arr.sort((a, b) => {
        const priorityDiff = getPriority(b) - getPriority(a);
        if (priorityDiff !== 0) return priorityDiff;

        const activityDiff = Number(hasActivity(b)) - Number(hasActivity(a));
        if (activityDiff !== 0) return activityDiff;

        if (hasActivity(a) && hasActivity(b)) {
          const lastDiff = lastActivity(b) - lastActivity(a);
          if (lastDiff !== 0) return lastDiff;
        }

        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      });
    }
    return arr;
  }, [filtered, sort]);

  return {
    list: sorted,
    shown: sorted.length,
    total: bugs.length,
    onlyMine,
    setOnlyMine,
    showClosed,
    setShowClosed,
    onlyUrgent,
    setOnlyUrgent,
    sort,
    setSort,
  };
}
